import { useState, useEffect, useCallback, useMemo } from 'react';
import { Radio, X, ChevronRight, Check, RotateCcw, Minus, Anchor, Ship, Waves } from 'lucide-react';
import { useFlashDeck, type FlashCard } from '@/hooks/useFlashDeck';
import {
  getConsent, setConsent, isDismissedSession, dismissSession,
  getLearned, toggleLearned, resetLearned, getActiveDeck,
  type FlashDeckType,
} from '@/lib/flashMemoryStore';

import { useIsMobile } from '@/hooks/use-mobile';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { TickerSettings, type TickerSettingsData } from '@/components/TickerSettings';

const QUESTION_MS = 5000;
const ANSWER_MS = 4000;

type Phase = 'consent' | 'active' | 'expanded' | 'minimized' | 'hidden';

const CATEGORY_COLORS: Record<string, string> = {
  general: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30',
  radiotelephony: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
  asn: 'bg-red-500/20 text-red-400 border-red-500/30',
  phonetic: 'bg-violet-500/20 text-violet-400 border-violet-500/30',
  navigation: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  balisage: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
  meteo: 'bg-sky-500/20 text-sky-400 border-sky-500/30',
  securite: 'bg-red-500/20 text-red-400 border-red-500/30',
  reglementation: 'bg-violet-500/20 text-violet-400 border-violet-500/30',
  signalisation: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
  ecluses: 'bg-teal-500/20 text-teal-400 border-teal-500/30',
  technique: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
  environnement: 'bg-green-500/20 text-green-400 border-green-500/30',
};

const CATEGORY_LABELS: Record<string, string> = {
  general: 'Général',
  radiotelephony: 'Radio',
  asn: 'ASN',
  phonetic: 'Phonétique',
  navigation: 'Navigation',
  balisage: 'Balisage',
  meteo: 'Météo',
  securite: 'Sécurité',
  reglementation: 'Réglementation',
  signalisation: 'Signalisation',
  ecluses: 'Écluses',
  technique: 'Technique',
  environnement: 'Environnement',
};

const DECK_LABELS: Record<FlashDeckType, { label: string; icon: typeof Radio }> = {
  crr: { label: 'CRR', icon: Radio },
  cotier: { label: 'Côtier', icon: Anchor },
  fluvial: { label: 'Fluvial', icon: Ship },
  jetski: { label: 'Jet Ski', icon: Waves },
};

function pickNext(
  current: number,
  cards: FlashCard[],
  learned: Record<string, boolean>,
  hideLearned: boolean,
): number {
  const pool: number[] = [];
  const learnedPool: number[] = [];

  cards.forEach((q, i) => {
    if (i === current) return;
    if (learned[q.id]) {
      learnedPool.push(i);
    } else {
      pool.push(i);
    }
  });

  if (!hideLearned) {
    learnedPool.forEach((i) => {
      if (Math.random() < 0.2) pool.push(i);
    });
  }

  if (pool.length === 0) {
    return (current + 1) % cards.length;
  }
  return pool[Math.floor(Math.random() * pool.length)];
}

export const CRRFlashTicker = () => {
  const isMobile = useIsMobile();
  const isFr = true; // French maritime exam content
  const [activeDeck, setActiveDeckState] = useState<FlashDeckType>(getActiveDeck);
  const { data: cards = [], isLoading } = useFlashDeck(activeDeck);

  const [phase, setPhase] = useState<Phase>(() => {
    if (isDismissedSession()) return 'hidden';
    if (getConsent()) return isMobile ? 'minimized' : 'active';
    return 'consent';
  });

  const [index, setIndex] = useState(0);
  const [showAnswer, setShowAnswer] = useState(false);
  const [transitioning, setTransitioning] = useState(false);
  const [learned, setLearned] = useState<Record<string, boolean>>(() => getLearned(activeDeck));
  const [settings, setSettings] = useState<TickerSettingsData>({ hideLearned: false, reducedMotion: false });

  // Sync learned state when deck changes
  useEffect(() => {
    setLearned(getLearned(activeDeck));
    setIndex(0);
    setShowAnswer(false);
  }, [activeDeck]);

  const allLearned = useMemo(
    () => cards.length > 0 && cards.every((q) => learned[q.id]),
    [learned, cards],
  );

  const advance = useCallback(() => {
    if (cards.length === 0) return;
    setTransitioning(true);
    setTimeout(() => {
      setIndex((prev) => pickNext(prev, cards, learned, settings.hideLearned));
      setShowAnswer(false);
      setTransitioning(false);
    }, settings.reducedMotion ? 0 : 300);
  }, [learned, cards, settings.hideLearned, settings.reducedMotion]);

  useEffect(() => {
    if (phase !== 'active' || cards.length === 0) return;
    const timer = setTimeout(
      () => {
        if (!showAnswer) {
          setShowAnswer(true);
        } else {
          advance();
        }
      },
      showAnswer ? ANSWER_MS : QUESTION_MS,
    );
    return () => clearTimeout(timer);
  }, [showAnswer, phase, index, advance, cards.length]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && (phase === 'expanded' || phase === 'active')) {
        setPhase('minimized');
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [phase]);

  const handleConsent = (accepted: boolean) => {
    if (accepted) {
      setConsent(true);
      setPhase(isMobile ? 'minimized' : 'active');
    } else {
      dismissSession();
      setPhase('hidden');
    }
  };

  const handleToggleLearned = () => {
    if (!current) return;
    const updated = toggleLearned(current.id, activeDeck);
    setLearned({ ...updated });
  };

  const handleResetLearned = () => {
    resetLearned(activeDeck);
    setLearned({});
  };

  // Listen for deck changes from other components
  useEffect(() => {
    const handleStorage = (e: StorageEvent) => {
      if (e.key === 'crr-flash-active-deck' && e.newValue) {
        const newDeck = e.newValue as FlashDeckType;
        if (['crr', 'cotier', 'fluvial', 'jetski'].includes(newDeck)) {
          setActiveDeckState(newDeck);
        }
      }
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  if (phase === 'hidden' || isLoading || cards.length === 0) return null;

  const current = cards[index % cards.length];
  if (!current) return null;

  const q = isFr ? current.question_fr : current.question_en;
  const a = isFr ? current.answer_fr : current.answer_en;
  const ctx = isFr ? (current.explanation_fr || current.context_fr) : (current.explanation_en || current.context_en);
  const useCases = isFr ? (current.use_cases_fr || []) : (current.use_cases_en || []);
  const isLearned = !!learned[current.id];
  const learnedCount = Object.keys(learned).length;

  const deckInfo = DECK_LABELS[activeDeck];
  const DeckIcon = deckInfo.icon;

  // ── Consent Card ──
  if (phase === 'consent') {
    return (
      <div className="fixed bottom-4 left-4 z-40 max-w-xs" role="complementary" aria-label="Flash Questions">
        <div className="rounded-lg border border-primary/30 bg-background/95 backdrop-blur-sm shadow-lg shadow-primary/10 p-3">
          <div className="flex items-center gap-1.5 mb-2">
            <Radio className="w-4 h-4 text-primary" />
            <span className="text-xs font-orbitron font-bold text-primary tracking-widest uppercase">Flash Maritime</span>
          </div>
          <p className="text-xs text-muted-foreground leading-relaxed tracking-wide mb-1.5">
            Apprenez les termes maritimes en naviguant sur le site.
          </p>
          <p className="text-[9px] text-muted-foreground/50 leading-relaxed tracking-wide mb-3">
            Contenu éducatif non officiel — consultez les sources officielles.
          </p>
          <div className="flex gap-2">
            <Button size="sm" className="text-xs h-7 px-3 bg-primary text-primary-foreground hover:bg-primary/90" onClick={() => handleConsent(true)} aria-label="Activer les flash questions">
              Activer
            </Button>
            <Button size="sm" variant="ghost" className="text-xs h-7 px-3 text-muted-foreground" onClick={() => handleConsent(false)} aria-label="Pas maintenant">
              Pas maintenant
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // ── Minimized Pill ──
  if (phase === 'minimized') {
    return (
      <div className="fixed bottom-4 left-4 z-40" role="complementary" aria-label="Flash Questions">
        <button
          onClick={() => setPhase('active')}
          className="flex items-center gap-1.5 rounded-full border border-primary/30 bg-background/95 backdrop-blur-sm shadow-lg shadow-primary/10 px-3 py-1.5 hover:border-primary/60 transition-colors"
          aria-label="Ouvrir les flash questions"
        >
          <DeckIcon className={`w-3.5 h-3.5 text-primary ${settings.reducedMotion ? '' : 'motion-safe:animate-pulse'}`} />
          <span className="text-[10px] font-orbitron font-bold text-primary tracking-widest">⚓ {deckInfo.label}</span>
        </button>
      </div>
    );
  }

  // ── Expanded View ──
  if (phase === 'expanded') {
    return (
      <div className="fixed bottom-4 left-4 z-40 max-w-sm" role="complementary" aria-label="Flash Questions">
        <div className="rounded-lg border border-primary/30 bg-background/95 backdrop-blur-sm shadow-lg shadow-primary/10">
          <div className="flex items-center justify-between px-3 pt-2.5 pb-1">
            <div className="flex items-center gap-1.5">
              <DeckIcon className="w-3.5 h-3.5 text-primary" />
              <span className="text-[10px] font-orbitron font-bold text-primary tracking-widest uppercase">
                {current.acronym || `Flash ${deckInfo.label}`}
              </span>
            </div>
            <div className="flex items-center gap-1">
              <Badge variant="outline" className={`text-[9px] px-1.5 py-0 h-4 border ${CATEGORY_COLORS[current.category] || ''}`}>
                {CATEGORY_LABELS[current.category] || current.category}
              </Badge>
              <TickerSettings onSettingsChange={setSettings} />
              <button onClick={() => setPhase('minimized')} className="p-1 rounded-full hover:bg-accent/20 text-muted-foreground hover:text-foreground transition-colors" aria-label="Réduire">
                <Minus className="w-3 h-3" />
              </button>
            </div>
          </div>

          {/* Deck switcher mini */}
          <div className="flex gap-1 px-3 pb-1">
            {(Object.entries(DECK_LABELS) as [FlashDeckType, typeof DECK_LABELS[FlashDeckType]][]).map(([key, cfg]) => {
              const Icon = cfg.icon;
              return (
                <button
                  key={key}
                  onClick={() => setActiveDeckState(key)}
                  className={`flex items-center gap-0.5 text-[9px] px-1.5 py-0.5 rounded transition-colors ${
                    activeDeck === key
                      ? 'bg-primary/20 text-primary font-semibold'
                      : 'text-muted-foreground hover:text-foreground hover:bg-accent/20'
                  }`}
                >
                  <Icon className="w-2.5 h-2.5" />
                  {cfg.label}
                </button>
              );
            })}
          </div>

          <div className="px-3 pb-3" aria-live="polite">
            <p className="text-sm text-foreground leading-relaxed tracking-wide font-medium">{q}</p>
            <p className="text-sm text-primary font-semibold mt-1.5 leading-relaxed tracking-wide">→ {a}</p>
            <p className="text-xs text-muted-foreground mt-2 leading-relaxed tracking-wide italic">{ctx}</p>

            {useCases.length > 0 && (
              <ul className="mt-2 space-y-1">
                {useCases.map((uc, i) => (
                  <li key={i} className="flex items-start gap-1.5 text-[10px] text-muted-foreground/80 tracking-wide">
                    <span className="text-primary mt-0.5">•</span>
                    {uc}
                  </li>
                ))}
              </ul>
            )}

            <div className="flex items-center justify-between mt-3 pt-2 border-t border-border/50">
              <button
                onClick={handleToggleLearned}
                className={`flex items-center gap-1 text-xs px-2 py-1 rounded-md transition-colors ${isLearned ? 'bg-primary/20 text-primary' : 'text-muted-foreground hover:text-foreground hover:bg-accent/20'}`}
                aria-label={isLearned ? 'Marquer comme non appris' : 'Marquer comme appris'}
              >
                <Check className="w-3 h-3" />
                {isLearned ? 'Appris ✓' : 'Marquer appris'}
              </button>
              <div className="flex items-center gap-2">
                <span className="text-[9px] text-muted-foreground/60">{learnedCount}/{cards.length}</span>
                <button onClick={() => { advance(); setPhase('active'); }} className="flex items-center gap-0.5 text-xs text-primary hover:text-primary/80 transition-colors" aria-label="Question suivante">
                  Suivant
                  <ChevronRight className="w-3 h-3" />
                </button>
              </div>
            </div>

            {allLearned && (
              <div className="mt-2 pt-2 border-t border-primary/20 text-center">
                <p className="text-xs text-primary font-semibold">🎉 Tout appris ! Bravo !</p>
                <button onClick={handleResetLearned} className="flex items-center gap-1 mx-auto mt-1 text-[10px] text-muted-foreground hover:text-foreground transition-colors" aria-label="Recommencer">
                  <RotateCcw className="w-3 h-3" />
                  Recommencer
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ── Active Ticker (default) ──
  return (
    <div className="fixed bottom-20 md:bottom-4 left-4 z-40 max-w-[calc(50vw-1rem)] md:max-w-sm" role="complementary" aria-label="Flash Questions">
      <div
        className={`relative rounded-lg border border-primary/30 bg-background/95 backdrop-blur-sm shadow-lg shadow-primary/10 cursor-pointer ${settings.reducedMotion ? '' : 'transition-opacity duration-300'} ${transitioning ? 'opacity-0' : 'opacity-100'}`}
        onClick={() => setPhase('expanded')}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setPhase('expanded'); } }}
        tabIndex={0}
        role="button"
        aria-label="Voir les détails de la question"
      >
        <button
          onClick={(e) => { e.stopPropagation(); setPhase('minimized'); }}
          className="absolute -top-2 -right-2 p-1 rounded-full bg-background border border-primary/30 hover:bg-accent/20 text-muted-foreground hover:text-foreground transition-colors"
          aria-label="Réduire le ticker"
        >
          <X className="w-3 h-3" />
        </button>

        <div className="px-3 py-2.5" aria-live="polite">
          <div className="flex items-center gap-1.5 mb-1.5">
            <DeckIcon className={`w-3.5 h-3.5 text-primary ${settings.reducedMotion ? '' : 'motion-safe:animate-pulse'}`} />
            <span className="text-[10px] font-orbitron font-bold text-primary tracking-widest uppercase">⚓ Flash {deckInfo.label}</span>
            <span className="text-[9px] text-muted-foreground/60 ml-auto">24H</span>
          </div>

          <p className="text-xs text-foreground leading-relaxed tracking-wide">
            {q}
            {isLearned && <Check className="inline w-3 h-3 text-primary ml-1" />}
          </p>

          {showAnswer && (
            <p className={`text-xs text-primary font-semibold mt-1 leading-relaxed tracking-wide ${settings.reducedMotion ? '' : 'animate-in fade-in duration-500'}`}>
              → {a}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};
